import React from "react";
import { createPortal } from "react-dom";
import {
  getFocusableElements,
  isInteractive
} from "@cmp-src/mobile/MobileDrawer/helpers";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { cn } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";

const ANIM_MS = 220;

const classes = {
  Wrapper: "fixed inset-0 z-modal",
  Backdrop: "absolute inset-0 w-full h-full",
  Container:
    "absolute inset-x-0 bottom-0 flex justify-center pb-[env(safe-area-inset-bottom)] shadow-2xl",
  Inner: "w-full max-w-md space-y-2",
  InnerMd: "w-full max-w-[1000px] space-y-2",
  Panel:
    "rounded-t-2xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden pb-5",
  // Full-width sticky header = the drag handle area
  GrabArea:
    "sticky top-0 z-10 select-none bg-white cursor-grab active:cursor-grabbing",
  GrabAreaInner: "py-3 min-h-[44px] flex items-center justify-center",
  Grabber: "h-1 w-[60px] rounded-full bg-neutral-300",
  ContentScroller: "overflow-y-auto overscroll-contain relative bg-white",
  SecondCard:
    "overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5",
  FooterContent: "bg-white"
};

function isInDragHandle(el: Element | null): boolean {
  return !!(el && (el as HTMLElement).closest?.("[data-drag-handle]"));
}

export interface MobileDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the drawer mounts or unmounts, after its animation. */
  onVisibleChange?: (isVisible: boolean, isOpen: boolean) => void;
  /** "fit" (default) sizes it to the content; "full" takes the screen up to `revealGap`. */
  size?: "fit" | "full";
  /** Space kept above the drawer, in px. Default 120. */
  revealGap?: number;
  /** Lets Escape, the backdrop and dragging close it. Default true. */
  dismissible?: boolean;
  /** How far it must be dragged down to close, in px. Default 20. */
  dragCloseThreshold?: number;
  className?: string;
  /** Names the drawer. Default "Drawer". */
  ariaLabel?: string;
  children: React.ReactNode;
  /** Content under the panel, e.g. a separate cancel button. */
  below?: React.ReactNode;
  /** Element to portal into; the page body by default. */
  portalContainer?: Element | null;
  /** Drags only from the grabber instead of the whole panel. */
  dragOnTileOnly?: boolean;
  /** Lets the content scroll (and drags only from the grabber). */
  scrollableContent?: boolean;
  /** Lays the grabber over the top of the content. */
  contentUnderGrabber?: boolean;
  /** Dims the page and makes the drawer modal. Default true. */
  showBackdrop?: boolean;
  /** Fixed content above the scrolling content. */
  topContent?: React.ReactNode;
  /** Fixed content under the panel. */
  footerContent?: React.ReactNode;
  /** Class of the scrolling content. */
  containerClassName?: string;
  /** When true, removes the max-width constraint for full-width drawer */
  fullWidth?: boolean;
}

/**
 * The bottom sheet of the phone layouts: slides up over a dimmed backdrop and closes by
 * dragging it down, a tap on the backdrop or Escape.
 */
export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onOpenChange,
  onVisibleChange,
  size = "fit",
  revealGap = 120,
  dismissible = true,
  dragCloseThreshold = 20,
  className,
  ariaLabel = "Drawer",
  children,
  below,
  portalContainer,
  dragOnTileOnly = false,
  scrollableContent = false,
  contentUnderGrabber = false,
  showBackdrop = true,
  topContent,
  footerContent,
  containerClassName,
  fullWidth = false
}) => {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const panelRef = React.useRef<HTMLDivElement>(null);
  const grabAreaRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Force tile-only drag when scrollable, to avoid scroll/drag conflicts
  const effectiveDragOnTileOnly = dragOnTileOnly || scrollableContent;

  const { isVisible } = useCloseAnimationTimeout(isOpen, {
    defaultState: false,
    animationDuration: ANIM_MS
  });

  const [panelHeight, setPanelHeight] = React.useState(0);
  const [animY, setAnimY] = React.useState(0);
  const [dragY, setDragY] = React.useState(0);
  const [transitionOn, setTransitionOn] = React.useState(true);

  const dragging = React.useRef(false);
  const dragStartY = React.useRef(0);

  React.useLayoutEffect(() => {
    if (!isVisible) return;
    const measure = () => {
      const h = panelRef.current?.offsetHeight ?? 0;
      if (h && h !== panelHeight) setPanelHeight(h);
    };
    measure();
    const r = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(r);
  }, [isVisible, size, children, panelHeight]);

  React.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (!isVisible) return;
    if (isOpen) {
      const start =
        (panelRef.current?.offsetHeight ??
          (panelHeight || window.innerHeight)) + 20;
      setTransitionOn(false);
      setAnimY(start);
      setDragY(0);
      requestAnimationFrame(() => {
        setTransitionOn(true);
        setAnimY(0);
      });
    } else {
      const end =
        (panelRef.current?.offsetHeight ??
          (panelHeight || window.innerHeight)) + 20;
      setTransitionOn(true);
      setDragY(0);
      setAnimY(end);
    }
  }, [isVisible, isOpen]);

  // Focus moves into the drawer when it opens and back to where it was when it closes
  React.useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = requestAnimationFrame(() => {
      if (!panelRef.current?.contains(document.activeElement)) {
        panelRef.current?.focus();
      }
    });
    return () => {
      cancelAnimationFrame(focusTimer);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isVisible, dismissible, onOpenChange]);

  // Pointer handlers — attach to grab area or whole panel
  React.useEffect(() => {
    if (!isVisible) return;

    const target: HTMLElement | null = effectiveDragOnTileOnly
      ? grabAreaRef.current
      : panelRef.current;

    if (!target) return;

    const onPointerDown = (e: PointerEvent) => {
      const targetEl = e.target as Element;

      // If drag is allowed only from the tile, we don't need this guard
      if (!effectiveDragOnTileOnly) {
        const onHandle = isInDragHandle(targetEl);
        const interactive = isInteractive(targetEl);

        // Skip only when it's interactive AND not the handle
        if (interactive && !onHandle) return;
      }

      dragging.current = true;
      dragStartY.current = e.clientY;
      setTransitionOn(false);

      // prevent scroller kick-in when starting on the header
      if (isInDragHandle(targetEl)) e.preventDefault();

      try {
        target.setPointerCapture(e.pointerId);
      } catch {}
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const dy = e.clientY - dragStartY.current;
      setDragY(Math.max(0, dy));
    };

    const endDrag = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (dragY > (dragCloseThreshold ?? 120)) {
        setTransitionOn(true);
        const end =
          (panelRef.current?.offsetHeight ??
            (panelHeight || window.innerHeight)) + 20;
        setAnimY(end);
        setDragY(0);
        onOpenChange(false);
      } else {
        setTransitionOn(true);
        setDragY(0);
      }
    };

    target.addEventListener("pointerdown", onPointerDown);
    target.addEventListener("pointermove", onPointerMove);
    target.addEventListener("pointerup", endDrag);
    target.addEventListener("pointercancel", endDrag);

    return () => {
      target.removeEventListener("pointerdown", onPointerDown);
      target.removeEventListener("pointermove", onPointerMove);
      target.removeEventListener("pointerup", endDrag);
      target.removeEventListener("pointercancel", endDrag);
    };
  }, [
    dragY,
    dragCloseThreshold,
    panelHeight,
    onOpenChange,
    isVisible,
    effectiveDragOnTileOnly
  ]);

  React.useEffect(() => {
    onVisibleChange?.(isVisible, isOpen);
  }, [isVisible, isOpen]);

  if (!isVisible) return null;

  const root =
    portalContainer ?? (typeof document !== "undefined" ? document.body : null);

  const gap = Math.max(revealGap, 0);
  const maxHeight = `calc(100dvh - ${gap}px)`;
  const height = size === "full" ? maxHeight : "auto";

  const translateY = animY + dragY;

  const backDropMax = 0.4;
  const denom = (panelHeight || window.innerHeight) + 20;
  const backdropOpacity = backDropMax * (1 - Math.min(translateY / denom, 1));

  const transition = transitionOn
    ? `transform ${ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
    : "none";
  const backdropTransition = transitionOn
    ? `opacity ${ANIM_MS}ms ease`
    : "none";

  // Panel (animated shell) must not scroll
  const panelCommonStyle: React.CSSProperties = {
    transform: `translateY(${translateY}px)`,
    transition,
    maxHeight,
    height,
    overflow: "hidden",
    touchAction: "none",
    willChange: "transform"
  };

  // Content area scrolls independently
  const scrollerStyle: React.CSSProperties = {
    maxHeight: "inherit",
    WebkitOverflowScrolling: "touch",
    overflowY: scrollableContent ? "auto" : "hidden",
    touchAction: scrollableContent ? "pan-y" : "none"
  };

  const belowStyle: React.CSSProperties = {
    transform: `translateY(${translateY}px)`,
    transition,
    willChange: "transform"
  };

  // Keeps Tab inside a modal drawer
  const trapTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || !showBackdrop) return;
    const focusables = getFocusableElements(panelRef.current) as HTMLElement[];
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const sheet = (
    <div
      className={showBackdrop ? classes.Wrapper : undefined}
      role="dialog"
      aria-modal={showBackdrop}
      aria-label={ariaLabel}
      onKeyDown={trapTabKey}
    >
      {showBackdrop && (
        <button
          type="button"
          // The backdrop is for pointers; Escape closes the drawer from the keyboard
          tabIndex={-1}
          aria-label="Close"
          className={classes.Backdrop}
          style={{
            backgroundColor: "rgba(0,0,0,0.40)",
            opacity: backdropOpacity,
            transition: backdropTransition
          }}
          onClick={() => dismissible && onOpenChange(false)}
        />
      )}

      <div className={classes.Container}>
        <div
          className={cn(
            fullWidth ? "w-full" : isMd ? classes.InnerMd : classes.Inner,
            contentUnderGrabber ? "space-y-0" : "space-y-2"
          )}
        >
          <div
            ref={panelRef}
            tabIndex={-1}
            className={cn(
              classes.Panel,
              "focus:outline-none",
              contentUnderGrabber && "relative",
              className
            )}
            style={panelCommonStyle}
          >
            {/* ABSOLUTE, FULL-WIDTH GRAB HANDLE OVERLAY */}
            <div
              ref={grabAreaRef}
              className={
                contentUnderGrabber
                  ? // overlay on top of content
                    "absolute inset-x-0 top-0 z-10 cursor-grab select-none active:cursor-grabbing bg-white"
                  : // original sticky behavior
                    classes.GrabArea
              }
              aria-hidden
              aria-label="Drag to close"
              data-drag-handle=""
            >
              <div className={classes.GrabAreaInner} data-drag-handle="">
                <div className={classes.Grabber} data-drag-handle="" />
              </div>
            </div>
            {topContent && <div>{topContent}</div>}
            {/* Scrollable content lives UNDER the overlay when contentUnderGrabber = true */}
            <div
              ref={contentRef}
              className={cn(classes.ContentScroller, containerClassName)}
              style={scrollerStyle}
            >
              {children}
            </div>
            {below ? (
              <div className={cn("px-6")} style={belowStyle}>
                {below}
              </div>
            ) : null}
          </div>

          {footerContent && (
            <div className={classes.FooterContent}>{footerContent}</div>
          )}
        </div>
      </div>
    </div>
  );

  return root ? createPortal(sheet, root) : sheet;
};
